import type {
  PersistedClient,
  Persister,
} from '@tanstack/query-persist-client-core'
import { del, get, keys, set } from 'idb-keyval'

export async function removeIndexedDBCaches(prefix: string): Promise<void> {
  const matching = (await keys()).filter(
    key => typeof key === 'string' && key.startsWith(prefix),
  )
  await Promise.all(matching.map(key => del(key)))
}

export function createIndexedDBPersister(
  cacheKey: string,
  scopePrefix = cacheKey,
): Persister {
  return {
    persistClient: async (client: PersistedClient) => {
      try {
        await set(cacheKey, client)
      } catch (e) {
        console.warn('[QueryCache:IDB] Failed to persist client', e)
      }
    },
    restoreClient: async () => {
      try {
        const current = await get<PersistedClient>(cacheKey)
        if (current) return current
        // Migrate only this exact account/org/access scope. Keep old snapshots
        // until a successful write, including any queued offline commands.
        const legacyKeys = (await keys()).filter(
          key =>
            typeof key === 'string' &&
            key.startsWith(scopePrefix) &&
            !key.slice(scopePrefix.length).startsWith('schema-') &&
            key !== cacheKey,
        )
        const snapshots = await Promise.all(
          legacyKeys.map(key => get<PersistedClient>(key)),
        )
        return snapshots
          .filter((value): value is PersistedClient => !!value)
          .sort((a, b) => b.timestamp - a.timestamp)[0]
      } catch (e) {
        console.warn('[QueryCache:IDB] Failed to restore client', e)
        return undefined
      }
    },
    removeClient: async () => {
      try {
        await removeIndexedDBCaches(scopePrefix)
      } catch (e) {
        console.warn('[QueryCache:IDB] Failed to remove client', e)
      }
    },
  }
}
